import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AutoTablePageableResponse } from '@muchencute/mrrs-types';
import { Product, ProductStatus, ProductCompany } from './product-models';
import { AuthService } from '../auth/auth.service';
import * as _ from 'lodash';
import { CORE_CONFIG } from '../environment';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private _baseURL: string;

  companies = new BehaviorSubject<ProductCompany[]>([]);

  constructor(private httpClient: HttpClient,
    private authService: AuthService,
    @Inject(CORE_CONFIG) private coreConfig: any) {
    this._baseURL = `${this.coreConfig.endpoint}api/v1/product`;
    this._getCompanyList().subscribe(cs => this.companies.next(cs));
  }

  newProducts(formData: FormData): Observable<any> {
    return this.httpClient.post(`${this._baseURL}s`, formData);
  }

  newProduct(product: Product): Observable<Product> {
    return this.httpClient.post<Product>(`${this._baseURL}`, product);
  }

  updateProduct(product: Product): Observable<Product> {
    return this.httpClient.put<Product>(`${this._baseURL}/${product.code}`, product);
  }

  updateProductViews(code: number): Observable<any> {
    return this.httpClient.put(`${this._baseURL}views/${code}`, {});
  }

  removeProduct(code: string | string[]): Observable<any> {
    if (_.isArray(code)) {
      return this.httpClient.delete(`${this._baseURL}`, {
        params: {
          code: code
        }
      });
    } else {
      return this.httpClient.delete(`${this._baseURL}/${code}`);
    }
  }

  updateProductStatus(code: string | string[], status: ProductStatus): Observable<any> {
    if (_.isArray(code)) {
      return this.httpClient.patch(`${this._baseURL}`, {}, {
        params: {
          code: code,
          status: status
        }
      });
    } else {
      return this.httpClient.patch(`${this._baseURL}/${code}`, {}, {
        params: {
          status: status
        }
      });
    }
  }

  getProductList(params: { [param: string]: any }): Observable<AutoTablePageableResponse<Product>> {
    return this.httpClient.get<AutoTablePageableResponse<Product>>(`${this._baseURL}s`, {
      params: params
    });
  }

  getProduct(code: number): Observable<Product> {
    return this.httpClient.get<Product>(`${this._baseURL}/${code}`);
  }

  getProducts(codes: number[]): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this._baseURL}`, {
      params: {
        code: codes.map(c => c + '')
      }
    });
  }

  recommendProduct(code: string | string[], isRecommended: boolean): Observable<any> {
    if (!_.isArray(code)) {
      return this.httpClient.patch(`${this._baseURL}/${code}`, {}, {
        params: {
          recommend: isRecommended + ''
        },
      });
    } else {
      return this.httpClient.patch(`${this._baseURL}`, {}, {
        params: {
          code: code,
          recommend: isRecommended + ''
        }
      });
    }
  }

  getSuggests(keywords: string): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.coreConfig.endpoint}api/v1/productsuggests`, {
      params: {
        keywords: keywords
      }
    });
  }

  getCompaniesTemporary(params: { [name: string]: string }): Observable<ProductCompany[]> {
    return this.httpClient.get<ProductCompany[]>(`${this.coreConfig.endpoint}api/v1/companies`, { params: params });
  }

  private _getCompanyList(): Observable<ProductCompany[]> {
    return this.httpClient.get<ProductCompany[]>(`${this.coreConfig.endpoint}api/v1/companies`);
  }

}
